import React from 'react';
import {
  Activity,
  CheckCircle2,
  Cloud,
  Database,
  Search,
  Server,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

const CHECK_META = {
  firestore: {
    label: 'Firestore',
    description: 'Latest program metadata readable',
    icon: Database,
  },
  analytics_service: {
    label: 'Analytics Service',
    description: 'Tag backend liveness',
    icon: Server,
  },
  search_service: {
    label: 'Search Service',
    description: 'FTV search API reachable',
    icon: Search,
  },
  r2_storage: {
    label: 'R2 Storage',
    description: 'Verified by analytics service against Cloudflare R2',
    icon: Cloud,
  },
};

const statusBadgeClass = (status) => {
  if (status === 'healthy') return 'healthy';
  if (status === 'degraded') return 'degraded';
  return 'unhealthy';
};

const StatusIcon = ({ status }) => {
  if (status === 'healthy') return <CheckCircle2 size={12} />;
  if (status === 'degraded') return <AlertTriangle size={12} />;
  return <XCircle size={12} />;
};

const formatDetail = (check) => {
  if (check.error) return check.error;
  if (check.message) {
    return check.source ? `${check.message} (via ${check.source})` : check.message;
  }
  if (check.checked_key) {
    const detail = `Checked: ${check.bucket || 'R2'}/${check.checked_key}`;
    return check.source ? `${detail} (via ${check.source})` : detail;
  }
  if (check.sample_doc_id) return `Latest doc: ${check.sample_doc_id}`;
  if (check.http_status) return `HTTP ${check.http_status}`;
  return check.source ? `No additional details (via ${check.source})` : 'No additional details';
};

export const ServiceHealthSection = ({ data, loading, error, lastUpdated }) => {
  const checks = data?.checks || {};
  const overallStatus = data?.status || 'unknown';

  return (
    <section style={{ marginBottom: '24px' }}>
      <div className="glass-card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(16, 185, 129, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary-emerald)',
            }}>
              <Activity size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#ffffff' }}>Platform Service Health</h2>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                Firestore, analytics, search, and R2 checks from FTV server
              </p>
            </div>
          </div>

          <span className={`badge-status ${statusBadgeClass(overallStatus)}`}>
            <StatusIcon status={overallStatus} />
            {overallStatus}
          </span>
        </div>
      </div>

      {error && (
        <div className="glass-card" style={{
          marginBottom: '16px',
          borderColor: 'rgba(239, 68, 68, 0.4)',
          background: 'rgba(30, 10, 10, 0.85)',
        }}>
          <p style={{ fontSize: '0.88rem', color: '#fca5a5' }}>{error}</p>
        </div>
      )}

      {loading && !data && !error ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '32px 20px' }}>
          <div className="pulse-dot" style={{ width: '16px', height: '16px', marginBottom: '12px' }} />
          <p style={{ color: 'var(--text-muted)' }}>Loading service health...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {Object.entries(CHECK_META).map(([key, meta]) => {
            const check = checks[key] || { status: 'unknown' };
            const Icon = meta.icon;

            return (
              <div key={key} className="glass-card">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '8px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--primary-emerald)',
                    }}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ffffff' }}>{meta.label}</h3>
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>{meta.description}</p>
                    </div>
                  </div>

                  <span className={`badge-status ${statusBadgeClass(check.status)}`}>
                    <StatusIcon status={check.status} />
                    {check.status}
                  </span>
                </div>

                <div style={{
                  background: 'rgba(6, 18, 14, 0.6)',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '6px' }}>
                    {typeof check.latency_ms === 'number' ? `${check.latency_ms} ms` : 'Latency unavailable'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-main)', lineHeight: 1.45 }}>
                    {formatDetail(check)}
                  </div>
                  {check.video_name && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '8px' }}>
                      Video: {check.video_name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lastUpdated && (
        <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '12px' }}>
          Service health updated: {lastUpdated.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          })}
        </p>
      )}
    </section>
  );
};
